import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { Mail, Edit2, Eye, Copy, AlertCircle } from 'lucide-react'

const EmailTemplateManagement = () => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewContent, setPreviewContent] = useState({ htmlContent: '', textContent: '' })
  const [previewLoading, setPreviewLoading] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm()

  // Watch for content changes
  const htmlContent = watch('htmlContent', '')
  const textContent = watch('textContent', '')
  const templateType = watch('templateType', '')

  // Template type descriptions
  const templateTypes = [
    { value: 'welcome_new_inside', label: 'Welcome - New Member (Inside Neighborhood)', description: 'First-time members who live inside the neighborhood' },
    { value: 'welcome_new_outside', label: 'Welcome - New Member (Outside Neighborhood)', description: 'First-time members who live outside the neighborhood' },
    { value: 'welcome_returning_inside', label: 'Welcome - Returning Member (Inside Neighborhood)', description: 'Returning members who live inside the neighborhood' },
    { value: 'welcome_returning_outside', label: 'Welcome - Returning Member (Outside Neighborhood)', description: 'Returning members who live outside the neighborhood' },
    { value: 'taj_reservation', label: 'Taj Mahal Reservation Confirmation', description: 'Confirmation email for Taj Mahal facility bookings' },
    { value: 'pavilion_reservation', label: 'Pavilion Reservation Confirmation', description: 'Confirmation email for Tennis Pavilion bookings' },
    { value: 'admin_new_membership', label: 'Admin - New Membership Notification', description: 'Notification sent to admin when a new member completes payment' }
  ]

  // Common variables available in ALL templates
  const commonVariables = [
    '{{gateCode}}',
    '{{poolManagementName}}',
    '{{poolManagementEmail}}',
    '{{poolManagementPhone}}',
    '{{poolManagementWebsite}}',
    '{{siteUrl}}',
    '{{homeUrl}}',
    '{{poolUrl}}',
    '{{tennisUrl}}',
    '{{eventsUrl}}',
    '{{bookingUrl}}',
    '{{membershipUrl}}',
    '{{contactUrl}}'
  ]

  // Template-specific variables for each template type
  const variablesByType = {
    'welcome_new_inside': ['{{firstName}}', '{{lastName}}', '{{membershipType}}', '{{membershipYear}}', '{{currentYear}}', '{{amountPaid}}'],
    'welcome_new_outside': ['{{firstName}}', '{{lastName}}', '{{membershipType}}', '{{membershipYear}}', '{{currentYear}}', '{{amountPaid}}'],
    'welcome_returning_inside': ['{{firstName}}', '{{lastName}}', '{{membershipType}}', '{{membershipYear}}', '{{currentYear}}', '{{amountPaid}}'],
    'welcome_returning_outside': ['{{firstName}}', '{{lastName}}', '{{membershipType}}', '{{membershipYear}}', '{{currentYear}}', '{{amountPaid}}'],
    'taj_reservation': ['{{contactName}}', '{{facilityName}}', '{{eventDate}}', '{{startTime}}', '{{endTime}}', '{{eventType}}', '{{numberOfGuests}}'],
    'pavilion_reservation': ['{{contactName}}', '{{facilityName}}', '{{eventDate}}', '{{startTime}}', '{{endTime}}', '{{eventType}}', '{{numberOfGuests}}'],
    'admin_new_membership': ['{{firstName}}', '{{lastName}}', '{{email}}', '{{phone}}', '{{dateOfBirth}}', '{{street}}', '{{city}}', '{{state}}', '{{zipCode}}', '{{fullAddress}}', '{{membershipType}}', '{{membershipStatus}}', '{{membershipYear}}', '{{currentYear}}', '{{amountPaid}}', '{{paymentMethod}}', '{{paymentReference}}', '{{paymentDate}}', '{{discountCode}}', '{{householdMembers}}', '{{householdMembersText}}', '{{emergencyContactName}}', '{{emergencyContactPhone}}', '{{emergencyContactRelationship}}', '{{adminMembersUrl}}']
  }

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/email-templates')
      setTemplates(response.data)
    } catch (err) {
      setError('Failed to load email templates. Please try again.')
      console.error('Error fetching templates:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  // Handle edit template
  const handleEdit = (template) => {
    setEditingTemplate(template)
    setValue('templateType', template.templateType)
    setValue('subject', template.subject)
    setValue('htmlContent', template.htmlContent)
    setValue('textContent', template.textContent)
    setValue('isActive', template.isActive)
    setShowModal(true)
  }

  // Handle create new template
  const handleCreate = () => {
    setEditingTemplate(null)
    reset({
      templateType: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      isActive: true
    })
    setShowModal(true)
  }

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false)
    setEditingTemplate(null)
    reset()
  }

  // Handle submit
  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      setError('')

      if (editingTemplate) {
        // Update existing template
        await axios.put(`/api/email-templates/${editingTemplate._id}`, data)
      } else {
        // Create new template
        await axios.post('/api/email-templates', data)
      }

      await fetchTemplates()
      handleCloseModal()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template. Please try again.')
      console.error('Error saving template:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle preview
  const handlePreview = async () => {
    try {
      setPreviewLoading(true)
      const response = await axios.post('/api/email-templates/preview', {
        htmlContent,
        textContent,
        templateType
      })
      setPreviewContent(response.data)
      setShowPreview(true)
    } catch (err) {
      setError('Failed to generate preview. Please try again.')
      console.error('Error generating preview:', err)
    } finally {
      setPreviewLoading(false)
    }
  }

  // Copy variable to clipboard
  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-600 mt-1">Manage email templates for member communications</p>
        </div>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          Create Template
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading templates...</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <div
              key={template._id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {templateTypes.find(t => t.value === template.templateType)?.label}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      template.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mt-1">
                    {templateTypes.find(t => t.value === template.templateType)?.description}
                  </p>
                  <p className="text-gray-900 font-medium mt-3">Subject: {template.subject}</p>
                  <div className="mt-2 space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Template-Specific Variables:</p>
                      <div className="flex flex-wrap gap-1">
                        {variablesByType[template.templateType]?.map((variable) => (
                          <span
                            key={variable}
                            className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded font-mono"
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Common Variables:</p>
                      <div className="flex flex-wrap gap-1">
                        {commonVariables.map((variable) => (
                          <span
                            key={variable}
                            className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded font-mono"
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(template)}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No email templates found.</p>
              <button
                onClick={handleCreate}
                className="btn btn-primary mt-4"
              >
                Create Your First Template
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 my-8">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              {/* Template Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Type
                </label>
                <select
                  {...register('templateType', { required: 'Template type is required' })}
                  disabled={editingTemplate !== null}
                  className="input"
                >
                  <option value="">Select a template type</option>
                  {templateTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors.templateType && (
                  <p className="text-red-600 text-sm mt-1">{errors.templateType.message}</p>
                )}
                {templateType && (
                  <p className="text-gray-600 text-sm mt-2">
                    {templateTypes.find(t => t.value === templateType)?.description}
                  </p>
                )}
              </div>

              {/* Template-Specific Variables */}
              {templateType && variablesByType[templateType] && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-primary-900 mb-2">
                        Template-Specific Variables:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {variablesByType[templateType].map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => copyVariable(variable)}
                            className="px-3 py-1 bg-white border border-primary-300 text-primary-700 text-sm rounded font-mono hover:bg-primary-100 transition-colors flex items-center gap-2"
                          >
                            {variable}
                            <Copy className="h-3 w-3" />
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-primary-700 mt-2">
                        Click a variable to copy it to your clipboard, then paste it into your email content.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Common Variables (available in all templates) */}
              {templateType && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900 mb-2">
                        Common Variables (available in all templates):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {commonVariables.map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => copyVariable(variable)}
                            className="px-3 py-1 bg-white border border-green-300 text-green-700 text-sm rounded font-mono hover:bg-green-100 transition-colors flex items-center gap-2"
                            title={
                              variable === '{{gateCode}}' ? 'Pool gate code from Settings' :
                              variable === '{{poolManagementName}}' ? 'Pool management company name from Settings' :
                              variable === '{{poolManagementEmail}}' ? 'Pool management company email from Settings' :
                              variable === '{{poolManagementPhone}}' ? 'Pool management company phone from Settings' :
                              variable === '{{poolManagementWebsite}}' ? 'Pool management company website from Settings' :
                              variable === '{{siteUrl}}' ? 'Main site URL' :
                              variable === '{{homeUrl}}' ? 'Home page link' :
                              variable === '{{poolUrl}}' ? 'Pool page link' :
                              variable === '{{tennisUrl}}' ? 'Tennis page link' :
                              variable === '{{eventsUrl}}' ? 'Events page link' :
                              variable === '{{bookingUrl}}' ? 'Facility reservation page link' :
                              variable === '{{membershipUrl}}' ? 'Membership page link' :
                              variable === '{{contactUrl}}' ? 'Contact page link' : ''
                            }
                          >
                            {variable}
                            <Copy className="h-3 w-3" />
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        These variables are automatically populated with current values. Hover over each to see what it represents.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <input
                  type="text"
                  {...register('subject', { required: 'Subject is required' })}
                  className="input"
                  placeholder="Enter email subject line"
                />
                {errors.subject && (
                  <p className="text-red-600 text-sm mt-1">{errors.subject.message}</p>
                )}
              </div>

              {/* HTML Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HTML Content
                </label>
                <textarea
                  {...register('htmlContent', { required: 'HTML content is required' })}
                  rows={12}
                  className="input font-mono text-sm"
                  placeholder="Enter HTML email content with variables like {{firstName}}"
                />
                {errors.htmlContent && (
                  <p className="text-red-600 text-sm mt-1">{errors.htmlContent.message}</p>
                )}
              </div>

              {/* Text Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plain Text Content
                </label>
                <textarea
                  {...register('textContent', { required: 'Text content is required' })}
                  rows={8}
                  className="input font-mono text-sm"
                  placeholder="Enter plain text email content (fallback for email clients that don't support HTML)"
                />
                {errors.textContent && (
                  <p className="text-red-600 text-sm mt-1">{errors.textContent.message}</p>
                )}
              </div>

              {/* Is Active */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Template is active (will be used for sending emails)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={previewLoading || !templateType || !htmlContent}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {previewLoading ? 'Generating...' : 'Preview with Sample Data'}
                </button>
                <div className="flex-1"></div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                >
                  {submitting ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Email Preview (Sample Data)</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">HTML Preview</h3>
                <div className="border border-gray-300 rounded-lg p-4 bg-white">
                  <div dangerouslySetInnerHTML={{ __html: previewContent.htmlContent }} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Plain Text Preview</h3>
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <pre className="whitespace-pre-wrap font-sans text-sm">
                    {previewContent.textContent}
                  </pre>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowPreview(false)}
                  className="btn btn-secondary"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailTemplateManagement
